import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoIosArrowBack, IoIosSearch } from "react-icons/io";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { BsCheckCircleFill, BsCircle } from "react-icons/bs";
import { Skeleton } from 'antd';
import httpClient from '../../utils/HttpClient';
import { RECEIVERS_URL } from '../../constants/Url';
import { useTranslation } from 'react-i18next';

export default function AddressListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const res = await httpClient.get(RECEIVERS_URL);
      const dataList = res?.data?.data || res?.data;
      if (Array.isArray(dataList)) {
        // Map backend data to frontend format
        const mapped = dataList.map(a => ({
          id: a._id,
          name: a.recipientName,
          phone: a.phone?.number || '',
          address: `${a.province || ''}${a.city || ''}${a.district || ''}${a.addressLine2 || ''}${a.addressLine1 || ''}`,
          isDefault: a.isDefault,
          raw: a
        }));
        setAddresses(mapped);
      }
    } catch (err) {
      console.error("Failed to fetch addresses:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await httpClient.patch(`${RECEIVERS_URL}/${id}/default`);
      fetchAddresses(); // Refresh list after setting default
    } catch (err) {
      console.error("Failed to set default address:", err);
    }
  };

  const handleDeleteClick = (id) => {
    setAddressToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!addressToDelete) return;
    setDeleteModalOpen(false);
    try {
      await httpClient.delete(`${RECEIVERS_URL}/${addressToDelete}`);
      fetchAddresses(); // Refresh list after deletion
    } catch (err) {
      console.error("Failed to delete address:", err);
    }
  };

  const handleSelect = (addr) => {
    // If they came from checkout, return to checkout with this address
    if (location.state?.fromCheckout || location.state?.buyNowItem || location.state?.paymentMethod) {
      navigate('/checkout', { state: { ...location.state, selectedAddress: addr } });
    }
  };

  return (
    <div className="w-full max-w-4xl bg-[#f7f8fa] lg:bg-transparent flex flex-col relative mx-auto lg:my-10 lg:px-8">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md flex items-center justify-center relative py-4 px-4 shadow-sm z-20 sticky top-0 transition-all duration-300 lg:hidden">
        <button 
          onClick={() => navigate(-1)} 
          className="absolute left-4 text-xl text-gray-800 hover:scale-110 hover:text-[#e80d44] transition-all duration-300"
        >
          <IoIosArrowBack />
        </button>
        <h1 className="text-lg font-medium text-gray-900 tracking-wider">{t("text_receiver_address")}</h1>
      </div>

      <div className="hidden lg:flex justify-between items-end mb-8">
        <h1 className="text-3xl font-bold text-gray-900 tracking-wider">{t("text_receiver_address")}</h1>
        <button 
          onClick={() => navigate('/add-address', { state: location.state })}
          className="bg-gradient-to-r from-[#e80d44] to-[#ff3b68] text-white px-8 py-2.5 rounded-full font-semibold text-sm"
        >
          {t("text_add_new_address")}
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white lg:bg-transparent px-4 lg:px-0 py-3 lg:py-0 mb-4 z-10 shadow-[0_4px_10px_rgba(0,0,0,0.02)] lg:shadow-none sticky top-[60px] lg:static">
        <div className="flex items-center bg-[#f5f5f5] rounded-full px-4 py-2.5 border-2 border-transparent focus-within:border-[#f3f4f6] focus-within:bg-white">
          <IoIosSearch className="text-gray-400 text-xl shrink-0" />
          <input 
            type="text" 
            placeholder={t("text_search")} 
            className="bg-transparent border-none outline-none ml-2 text-sm w-full text-gray-700 placeholder-gray-400"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Address List */}
      <div className="flex-1 overflow-y-auto px-4 lg:px-0 py-4 space-y-4 pb-24 lg:pb-10 grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
        {loading ? (
          <>
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] border-2 border-gray-100">
                <Skeleton active avatar={false} paragraph={{ rows: 2 }} />
              </div>
            ))}
          </>
        ) : addresses.filter(a => 
          (a.name || '').toString().includes(searchQuery) || 
          (a.address || '').toString().includes(searchQuery) || 
          (a.phone || '').toString().includes(searchQuery)
        ).map(addr => (
          <div key={addr.id} className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] border-2 border-gray-100 cursor-pointer group">
            <div onClick={() => handleSelect(addr)}>
              <div className="flex items-center gap-4 mb-2">
                <span className="text-base font-semibold text-gray-900 tracking-wide">{addr.name}</span>
                <span className="text-sm text-gray-600 font-medium tracking-wide">{addr.phone}</span>
              </div>
              <p className="text-sm text-gray-500 mb-5 leading-relaxed break-words line-clamp-2 pr-2">{addr.address}</p>
            </div>
            
            <div className="flex justify-between items-center border-t border-gray-50 pt-4 mt-2">
              <button 
                onClick={(e) => { e.stopPropagation(); handleSetDefault(addr.id); }}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                {addr.isDefault ? (
                  <>
                    <BsCheckCircleFill className="text-[#0ed189] text-lg" />
                    <span className="text-[#0ed189] text-sm">{t("text_default_address")}</span>
                  </>
                ) : (
                  <>
                    <BsCircle className="text-gray-300 text-lg" />
                    <span className="text-gray-500 text-sm">{t("text_set_default_address")}</span>
                  </>
                )}
              </button>
              
              <div className="flex items-center gap-2">
                <button 
                  onClick={(e) => { e.stopPropagation(); navigate(`/edit-address/${addr.id}`, { state: { ...location.state, addressData: addr.raw } }); }}
                  className="flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-[#e80d44] hover:bg-[#fff0f4] transition-all duration-300"
                >
                  <FiEdit2 className="text-[17px]" />
                </button>
                {!addr.isDefault && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDeleteClick(addr.id); }}
                    className="flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-300"
                  >
                    <FiTrash2 className="text-[17px]" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom Sticky Button (Mobile Only) */}
      <div className="fixed lg:hidden bottom-0 left-0 right-0 w-full bg-white/90 backdrop-blur-md border-t-2 border-black/5 p-4 z-20 shadow-[0_-8px_30px_-5px_rgba(0,0,0,0.08)]">
        <button 
          onClick={() => navigate('/add-address', { state: location.state })}
          className="w-full bg-gradient-to-r from-[#e80d44] to-[#ff3b68] text-white py-3.5 rounded-full font-semibold text-base hover:scale-[1.02] transition-all duration-300 active:scale-[0.98]"
        >
          {t("text_add_new_address")}
        </button>
      </div>

      {/* Custom Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity p-4">
          <div className="bg-white rounded-[24px] p-8 w-full max-w-md shadow-2xl transform transition-all scale-100">
            <h3 className="text-xl font-extrabold text-gray-900 mb-3 text-center">{t("text_remove_address")}</h3>
            <p className="text-base text-gray-500 mb-8 text-center leading-relaxed">{t("text_confirm_remove_address")}</p>
            <div className="flex gap-4">
              <button 
                onClick={() => setDeleteModalOpen(false)}
                className="flex-1 py-3.5 rounded-full text-base font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                {t("button_cancel")}
              </button>
              <button 
                onClick={confirmDelete}
                className="flex-1 py-3.5 rounded-full text-base font-bold text-white bg-[#e80d44] hover:bg-[#d00b3d] transition-colors"
              >
                {t("text_confirm") || "确认"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
